import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller } from 'react-hook-form'
import { DateFormat, CurrencyFormat, buildQueryString } from '../../../helpers/formatting'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import Select from 'react-select'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { setChangeMade } from '../../../redux/paymentSchedulePanelSlice'
import { setModal } from '../../../redux/modalSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import SpecialInstruction from '../../../components/elements/SpecialInstruction'
import PaymentDatePicker from "../../../components/PaymentDatePicker";
import { format } from 'date-fns';

export default function PaydownPayment(props) {
  const { loanId, refreshPaymentSchedule, navigateSlider, currentDate, customerInfo, paymentMethodHelperText } = props
  const requesting = useRef(false)
  const request = useRequest()
  const dispatch = useDispatch()
  const [responseData, setResponseData] = useState([])
  const [paydownDateChanged, setPaydownDateChanged] = useState(false);
  const [paydownQuoteResponseData, setPaydownQuoteResponseData] = useState([])
  const today = new Date().toISOString().split("T")[0];
  const { register, handleSubmit, control, watch, setValue, setError, formState: { errors }, clearErrors } = useForm({ defaultValues: { payment_date: today } });
  const [saving, setSaving] = useState(false)
  const [buttonClicked, setButtonClicked] = useState('');
  const dropdownOptions = useSelector(selectDropdownOptions);
  const [finalFormData, setFinalFormData] = useState({});
  const [finalResponseData, setFinalResponseData] = useState([])
  const [savingQuote, setSavingQuote] = useState(false)
  const [enableScheduleButton, setEnableScheduleButton] = useState(false)
  const [quote, setQuote] = useState(null);
  const [paymentMethodDdoId, setPaymentMethodDdoId] = useState(null);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isPaymentAmountChange, setIsPaymentAmountChange] = useState(false);
  const paymentMethodACHId = dropdownOptions.find((o) => o?.code_key === "payment_method_ach")?.id;

  useEffect(() => {
    let hasChange = false
    if ((paymentMethodDdoId !== defaultPaymentMethod?.value) || isPaymentAmountChange) {
      hasChange = true
    }
    dispatch(setChangeMade(hasChange))
  }, [paymentMethodDdoId, isPaymentAmountChange])

  useEffect(() => {
    const fetchData = async () => {
      // execute the code when the component is mounted
      let cDate = currentDate ?? null;
      let pDate = currentDate ? currentDate.split(" ")[0] : today;
      let pId = null;
      await getPrincipalPaydownInfo(loanId, cDate, pDate, pId, true);
    }
    fetchData();
  }, [])

  useEffect(() => {
    if (currentDate) {
      setSelectedDate(new Date((`${currentDate}`)))
    }
  }, [currentDate])

  useEffect(() => {
    setPaydownDateChanged(true)
    setEnableScheduleButton(false)
    setFinalResponseData([])
    if (selectedDate === null) {
      setSelectedDate(new Date())
    }
  }, [selectedDate])

  // the following methods will be used to get the principal paydown info when the paydown slider is opened
  const getPrincipalPaydownInfo = async (loanId, currentDate, paymentDate, paymentMethodDropdownOptionId, isFirstTime = false) => {
    if (requesting.current) return;
    requesting.current = true;

    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/principal_paydown_info`;

      let params = {
        current_date: currentDate,
        payment_date: paymentDate,
        payment_method_dropdown_option_id: paymentMethodDropdownOptionId
      }
      request.setEndpoint(endpoint + buildQueryString(params))
      const response = await request.get()
      const selDateFormat = format(selectedDate, 'yyyy-MM-dd')
      setResponseData(response?.data?.data);
      if (selDateFormat < response?.data?.data?.min_paydown_date || selDateFormat > response?.data?.data?.max_paydown_date) {
        setSelectedDate(new Date(response?.data?.data?.min_paydown_date + ' 00:00:00'));
      }
      setQuote(response?.data?.data?.max_paydown_amount)
      if (!defaultPaymentMethod) {
        const paymentMethod = dropdownOptions.filter((o) => response?.data?.data?.default_payment_method_dropdown_option_id === o.id).map((o) => ({ label: o.name, value: o.id }))[0];
        setDefaultPaymentMethod(paymentMethod);
        setPaymentMethodDdoId(paymentMethod?.value)
      }

      requesting.current = false;
      if (isFirstTime) {
        setValue('payment_amount', response?.data?.data?.max_paydown_amount)

        // re requesing the paydown info to get the quote
        await getPrincipalPaydownInfo(loanId, currentDate, response?.data?.data?.min_paydown_date, response?.data?.data?.default_payment_method_dropdown_option_id, false)
      }
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false;
    }
  }

  function serialize(obj, prefix) {
    const str = [];
    Object.keys(obj).forEach((key) => {
      const k = prefix ? `${prefix}[${key}]` : key;
      const v = obj[key];
      str.push(
        v !== null && typeof v === "object"
          ? serialize(v, k)
          : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
      );
    });
    return str.join("&");
  }

  const onSubmit = async (formData) => {
    if (paymentMethodACHId == paymentMethodDdoId && customerInfo?.ach_revocation_active) {
      dispatch(
        setModal({
          is_active: true,
          type: 'allow_ach_payments_modal',
          options: { customerId: customerInfo.id, hasBkConfirmed: customerInfo?.has_bk_confirmed }
        })
      )
    } else {
      setSaving(true)
      if (finalResponseData.length == 0) {
        try {
          formData.payment_date = format(selectedDate, 'yyyy-MM-dd');
          formData.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id.value;
          const queryString = serialize(formData);
          let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/principal_paydown_preview?${queryString}`;
          if (currentDate !== null) {
            endpoint += `&current_date=${currentDate}`;
          }
          request.setEndpoint(endpoint)
          setFinalFormData(formData)
          const response = await request.get(formData)
          if (response.status === 200 || response.status === 201) {
            setSaving(false)
            setFinalResponseData(response?.data?.data)
          } else {
            setSaving(false)
            dispatch(setNotification({ type: 'error', body: `Error: ${response.response?.data?.message}` }))
          }
        } catch (error) {
          setSaving(false)
          const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
          dispatch(setNotification({ type: 'error', body: errorMessage }))
        }
      } else if (finalResponseData.length > 0) {
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/principal_paydown`)
        try {
          if (currentDate !== null) {
            formData['current_date'] = currentDate;
          }
          formData.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id.value;
          formData.payment_date = format(selectedDate, 'yyyy-MM-dd');
          const response = await request.post(formData)
          if (response.status === 200 || response.status === 201) {
            setSaving(false)
            dispatch(setNotification({ type: 'success', body: '' }))
            navigateSlider("form_submit")
            refreshPaymentSchedule()
          } else {
            setSaving(false)
            dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
          }
        } catch (error) {
          setSaving(false)
          const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
          dispatch(setNotification({ type: 'error', body: errorMessage }))
        }
      }
    }
  };

  const checkChangesMade = () => {
    navigateSlider("");
  }

  const getPaydownQuote = async (loanId, isFirstTime = false) => {
    if (requesting.current) return;
    try {
      const currentFormData = watch();
      let params = {
        payment_date: format(selectedDate, 'yyyy-MM-dd'),
        current_date: currentDate,
        payment_method_dropdown_option_id: currentFormData?.payment_method_dropdown_option_id?.value
      }
      if (!currentFormData?.payment_date) {
        setError("payment_date", { type: "manual", message: "Field is required" });
        return;
      } else {
        clearErrors("payment_date");
      }
      requesting.current = true;
      setSavingQuote(true)
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/principal_paydown_info` + buildQueryString(params);

      request.setEndpoint(endpoint)
      const response = await request.get()
      setPaydownQuoteResponseData(response?.data?.data);
      setQuote(response?.data?.data?.max_paydown_amount)
      setEnableScheduleButton(true)
      if (isFirstTime) {
        setValue('payment_amount', response?.data?.data?.max_paydown_amount)
      }
      requesting.current = false;
      setPaydownDateChanged(false)
      setSavingQuote(false)
    } catch (error) {
      setSavingQuote(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false;
    }
  }

  useEffect(() => {
    const fetchAndHandlePaydownInfo = async () => {
      try {
        await getPaydownQuote(loanId); // Wait for this call to complete

        if (responseData?.min_paydown_date) {
          const todaysDate = new Date();
          const formatSelectedDate = format(selectedDate, 'yyyy-MM-dd');
          const formatTodaysDate = format(todaysDate, 'yyyy-MM-dd');

          if (formatSelectedDate === formatTodaysDate && responseData?.min_paydown_date > formatTodaysDate) {
            setSelectedDate(new Date(`${responseData?.min_paydown_date} 00:00:00`));
          }
        }
      } catch (error) {
        console.error('Error fetching principal paydown info:', error);
      }
    };

    if (paymentMethodDdoId != null) {
      fetchAndHandlePaydownInfo();
    }
  }, [paymentMethodDdoId]);

  return (
    <div className="pl-5 py-3">
      {responseData.length == 0 && <div className="is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }}>--- Loading ---</div>}

      {responseData.length != 0 && (
        <>
          <SpecialInstruction content={"<span>" +
            "Typically a paydown goes solely against the principal, however there are some exceptions:" + "<br>" +
            "1) Outstanding fees will be paid first" + "<br>" +
            "2) If deferred payment in future, un-defer payment and apply $$ to that payment first (no fees applied) <br>" +
            "<span class='pl-5'> a) IF paydown covers whole deferred payment, then extra becomes \"Paydown\" against principal <br> </span>" +
            "<span class='pl-5'> b) IF paydown does NOT cover whole deferred payment, then apply as much as we can to original payment, then remainder on deferred payment <br> </span>" +
            " </span>"}
          />
          <form onSubmit={handleSubmit(onSubmit)} id="paydown_payment_form" data-testid="paydown-payment-form">
            <p className="title is-5">Paydown Payment</p>
            <table className="table is-narrow table-payment-schedule-panel">
              <thead>
                <tr>
                  <td><strong>Bank Name:</strong> {customerInfo?.bank_name}, <strong>Account Number:</strong> ###########{customerInfo?.bank_account_number_last_four}</td>
                </tr>
                <tr>
                  <th>
                    Payment Method <span
                      className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                      data-tooltip={paymentMethodHelperText}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ zIndex: 15, position: 'relative' }} data-testid="paydown-payment-method">
                    <Controller
                      name="payment_method_dropdown_option_id"
                      defaultValue={dropdownOptions.filter((o) => responseData?.default_payment_method_dropdown_option_id == o.id).map((o) => ({ label: o.name, value: o.id }))[0]}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          {...register('payment_method_dropdown_option_id', { required: true })}
                          options={dropdownOptions.filter((o) => responseData?.payment_method_dropdown_option_ids.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))}
                          isSearchable={false}
                          classNames={reactSelectClassNames()}
                          onChange={(val) => {
                            field.onChange({ label: val?.label, value: val?.value })
                            setPaymentMethodDdoId(val?.value)
                          }}
                          menuPosition="fixed"
                          form="paydown_payment_form"
                          styles={{
                            container: (base) => ({ ...base, width: '100%' }),
                            menu: ({ ...css }) => ({ ...css, width: '100%' }),
                          }}
                        />
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <table className="table is-narrow table-payment-schedule-panel">
              <tbody>
                <tr>
                  <td colSpan={2}><b>Outstanding Principal</b> <CurrencyFormat value={responseData?.outstanding_principal} /></td>
                  <td colSpan={1}>
                    {quote != null ? (
                      <><b>Max Paydown Amount (?)</b> <CurrencyFormat value={quote} /></>
                    ) : null}
                  </td>
                  <td>
                    <div className="is-inline-block has-text-right">
                      <button
                        type="button"
                        data-testid="paydown-payment-update-max"
                        className="button is-link is-small mr-1"
                        onClick={() => getPaydownQuote(loanId)}
                        disabled={(savingQuote || !paydownDateChanged) && 'disabled'}
                      >
                        <span className="mx-3">{savingQuote ? 'Updating Max Amount' : 'Update Max Amount'}</span>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Date</td>
                  <td data-testid="paydown-payment-date">
                    <Controller
                      name="payment_date"
                      control={control}
                      render={({ field }) => (
                        <PaymentDatePicker
                          paymentMethodDdoId={paymentMethodDdoId}
                          min={`${responseData?.min_paydown_date}T00:00:00`}
                          max={`${responseData?.max_paydown_date}T00:00:00`}
                          selectedDate={selectedDate}
                          setSelectedDate={setSelectedDate}
                        />
                      )}
                    />
                  </td>
                  <td>Can&apos;t go past <DateFormat dateString={responseData?.max_paydown_date} /></td>
                  <td>
                    <div className="has-text-right">
                      <button
                        type="button"
                        data-testid="paydown-payment-cancel"
                        className="button has-background-grey has-text-white is-small mr-1"
                        onClick={() => checkChangesMade()}
                      >
                        <span className="mx-3">Cancel</span>
                      </button>
                      {finalResponseData.length == 0 && (
                        <button
                          type="submit"
                          data-testid="paydown-payment-preview"
                          className="button is-link is-small mr-2"
                          name="preview"
                          form="paydown_payment_form"
                          onClick={() => setButtonClicked('preview')}
                          disabled={(quote == null) || (saving && 'disabled') || (enableScheduleButton == false && 'disabled')}
                        >
                          <span className="mx-3">Preview</span>
                        </button>
                      )}
                      {finalResponseData.length > 0 && (
                        <button
                          type="submit"
                          data-testid="paydown-payment-schedule"
                          className="button is-link is-small mr-1"
                          name="schedule"
                          form="paydown_payment_form"
                          onClick={() => setButtonClicked('schedule')}
                          disabled={saving && 'disabled'}
                        >
                          <span className="mx-3">{saving ? 'Scheduling' : 'Schedule'}</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Amount $</td>
                  <td>
                    <Controller
                      name="payment_amount"
                      control={control}
                      defaultValue={"0"}
                      render={({ field }) => (
                        <input
                          {...field}
                          data-testid="paydown-payment-amount"
                          name="payment_amount"
                          className="input is-small"
                          type="number"
                          form="paydown_payment_form"
                          min={0}
                          max={paydownQuoteResponseData?.max_paydown_amount}
                          step="0.01"
                          onChange={(val) => {
                            setIsPaymentAmountChange(true)
                            field.onChange(val)
                          }}
                          disabled={quote == null}
                        />
                      )}
                    />
                  </td>
                  <td>
                  </td>
                  <td>
                    {/* If amount is close to outstanding principal, and near a Regular payment, advise customer they maybe overpaying */}
                  </td>
                </tr>

              </tbody>
            </table>

            {finalResponseData.length > 0 && (
              <>
                <div className="divider m-0 mx-1 mb-3"></div>
                <table className="table is-striped is-fullwidth" data-testid="paydown-payment-preview-table">
                  <tbody>
                    <tr>
                      <th>Date</th>
                      <th>Method</th>
                      <th>Type</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>Loan Fee</th>
                      <th>Late Fee</th>
                      <th>NSF Fee</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                    {finalResponseData.map((row, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={index}>
                        <td><DateFormat dateString={row.scheduled_date} /></td>
                        <td>{dropdownOptions.filter((o) => o.id === row.payment_method_dropdown_option_id)[0].name}</td>
                        <td>{dropdownOptions.filter((o) => o.id === row.payment_type_dropdown_option_id)[0].name}</td>
                        <td><CurrencyFormat value={row.principal_amount} /></td>
                        <td><CurrencyFormat value={row.interest_amount} /></td>
                        <td><CurrencyFormat value={row.loan_fee} /></td>
                        <td><CurrencyFormat value={row.late_fee} /></td>
                        <td><CurrencyFormat value={row.nsf_fee} /></td>
                        <td><CurrencyFormat value={(+row.principal_amount + +row.interest_amount + +row.loan_fee + +row.late_fee + +row.nsf_fee).toFixed(2)} /></td>
                        <td>{dropdownOptions.filter((o) => o.id === row.payment_status_dropdown_option_id)[0].name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </form>
        </>
      )}
    </div>
  )
}
